import { FALLBACK_CHART, periodRange, type ReportPeriod } from '@forecastify/shared';
import type { Translate } from '../../i18n';
import type { BuilderDraft, FilterKey } from './builder-screen';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 17 Oct 2026 · Ready-made reports — what Kockázatkezelés and the old       │
 * │ product's `timelog_report.xlsx` showed, as starting points.               │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Kockázatkezelés retired into three reports, and the old product's timelog workbook was five
 * sheets of the same shape. Rather than seeding rows into every workspace — a report is private to
 * its author (P-3), so a seeded one would belong to nobody — each is a TEMPLATE: picking it opens
 * the builder already set up, and saving makes it the reader's own.
 *
 * ⚠️ **Their periods are kept presets**, so a saved *Ismeretlen logolások* keeps reading the last
 * three months rather than the three months it was saved in.
 */

export const REPORT_TEMPLATE_IDS = [
  'unknownWorklogs',
  'unknownTasks',
  'loggingGaps',
  'lineItemLogs',
  'unknownByPerson',
  'unknownByTask',
] as const;
export type ReportTemplateId = (typeof REPORT_TEMPLATE_IDS)[number];

export function isTemplateId(value: string | null): value is ReportTemplateId {
  return value !== null && (REPORT_TEMPLATE_IDS as readonly string[]).includes(value);
}

/** A template's name — also the name a report started from it is given. */
export function templateName(t: Translate, id: ReportTemplateId): string {
  if (id === 'unknownWorklogs') return t('report.template.unknownWorklogs');
  if (id === 'unknownTasks') return t('report.template.unknownTasks');
  if (id === 'loggingGaps') return t('report.template.loggingGaps');
  if (id === 'lineItemLogs') return t('report.template.lineItemLogs');
  if (id === 'unknownByPerson') return t('report.template.unknownByPerson');
  return t('report.template.unknownByTask');
}

/** The draft a template opens the builder with, and which filter rows it shows. */
export function templateDraft(
  id: ReportTemplateId,
  today: string,
): { readonly draft: BuilderDraft; readonly filters: readonly FilterKey[] } {
  const base = (period: ReportPeriod): BuilderDraft => {
    const range = periodRange(period, today);
    return {
      source: 'worklog',
      dimension: 'assignee',
      dimensionMetafieldId: null,
      metric: 'hours',
      chart: FALLBACK_CHART,
      periodFrom: range.from,
      periodTo: range.to,
      period,
      filterProjectId: null,
      filterMemberId: null,
      filterMetafieldId: null,
      filterMetafieldValue: null,
      layout: 'timeline',
    };
  };
  /* ⚠️ *Nincs sortétel* — the hours no line item holds, and no recurring contract either. */
  const unknown = { filters: { lineItems: ['none'] } };

  if (id === 'unknownWorklogs') {
    return {
      draft: {
        ...base('last3Months'),
        ...unknown,
        timeline: {
          rows: ['assignee'],
          granularity: 'month',
          extras: [
            { kind: 'count', dimension: 'task' },
            { kind: 'list', dimension: 'task' },
          ],
        },
      },
      filters: ['lineItems'],
    };
  }
  if (id === 'unknownTasks') {
    return {
      draft: {
        ...base('last3Months'),
        ...unknown,
        dimension: 'task',
        timeline: {
          rows: ['task'],
          granularity: 'month',
          extras: [{ kind: 'list', dimension: 'assignee' }],
        },
      },
      filters: ['lineItems'],
    };
  }
  if (id === 'loggingGaps') {
    return {
      draft: {
        ...base('last3Months'),
        source: 'workday',
        metric: 'missing',
        timeline: { rows: ['assignee'], granularity: 'month', extras: [] },
      },
      filters: [],
    };
  }
  if (id === 'lineItemLogs') {
    return {
      draft: {
        ...base('year'),
        timeline: { rows: ['assignee', 'lineitem'], granularity: 'month', extras: [] },
      },
      filters: [],
    };
  }
  if (id === 'unknownByPerson') {
    return {
      draft: {
        ...base('year'),
        ...unknown,
        timeline: { rows: ['assignee', 'task'], granularity: 'month', extras: [] },
      },
      filters: ['lineItems'],
    };
  }
  return {
    draft: {
      ...base('year'),
      ...unknown,
      dimension: 'task',
      timeline: { rows: ['task', 'assignee'], granularity: 'month', extras: [] },
    },
    filters: ['lineItems'],
  };
}
